import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { GridRowSelectionModel } from '@mui/x-data-grid-premium';
import Actions from '../../../store/Actions';
import { getRowId } from '../utils';
import { OTCResponse } from '../../../types/generatedTypes';
import { useGridSelectionHandler } from '../../../components/Groups/useGridSelection';
import {
  OTCRow,
  UseOTCDataProps,
  UseOTCDataReturn,
  CalculatePnLFn,
  CalculateDaysSinceIllioUpdateFn,
  UpdatedCellsMap,
  DAYS_SINCE_ILLIO_UPDATE,
} from '../types';
import { showSnackbar } from '../../../context/Snackbar/SnackbarContext';
import { SnackbarType } from '../../../context/Snackbar/Types';
import {
  BASE_COLUMN_FIELDS,
  COLUMN_FIELDS,
  DATE_FORMATS,
} from '../OTCConstants';
import { parseNumberWithCommas } from '../../../utils/numberFormatter';
import { useGroups } from '../../../hooks/useGroups';
import { useExpandGroups } from '../../../components/Groups/groupUtils';

export const useOTCData = ({
  apiRef,
  displayData,
}: UseOTCDataProps): UseOTCDataReturn => {
  // Core data state
  const [otcData, setOTCData] = useState<OTCRow[]>([]);
  const [originalData, setOriginalData] = useState<Record<string, OTCRow>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [lastSyncDate, setLastSyncDate] = useState<string | null>(null);
  const [showInactiveInstruments, setShowInactiveInstruments] =
    useState<boolean>(false);

  // Groups functionality using centralized hook
  const {
    groups,
    setGroups,
    isGroupingEnabled,
    setIsGroupingEnabled,
    isGroupsModalOpen,
    loadGroupsFromBackend,
    handleOpenGroupsModal,
    handleCloseGroupsModal,
    handleSaveGroups,
  } = useGroups({ pageType: 'otc' });

  // Calculate P&L for a row given date fields
  const calculatePnL: CalculatePnLFn = useCallback(
    (row: OTCRow, dateFields: string[]): number | null => {
      // If no date fields, return null
      if (!dateFields || dateFields.length === 0) return null;

      // Find the first valid latest value
      let latestValue: number | null = null;
      let earliestValue: number | null = null;

      // Loop through dates from newest to oldest to find first valid latest value
      for (const dateField of dateFields) {
        const value = row[dateField];
        if (
          value !== undefined &&
          value !== null &&
          value !== '' &&
          !isNaN(parseNumberWithCommas(String(value)))
        ) {
          latestValue = parseNumberWithCommas(String(value));
          break;
        }
      }

      // Loop through dates from oldest to newest to find first valid earliest value
      for (const dateField of [...dateFields].reverse()) {
        const value = row[dateField];
        if (
          value !== undefined &&
          value !== null &&
          value !== '' &&
          !isNaN(parseNumberWithCommas(String(value)))
        ) {
          earliestValue = parseNumberWithCommas(String(value));
          break;
        }
      }

      // If either value is missing or zero, return null
      if (
        latestValue === null ||
        earliestValue === null ||
        earliestValue === 0
      ) {
        return null;
      }

      // Calculate percentage change
      return ((latestValue - earliestValue) / earliestValue) * 100;
    },
    []
  );

  // Calculate days since Illio update
  const calculateDaysSinceIllioUpdate: CalculateDaysSinceIllioUpdateFn =
    useCallback(
      (
        row: OTCRow,
        rowId: string,
        currentUpdatedCells: UpdatedCellsMap
      ): number | null => {
        // Get today's date for comparison
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to beginning of day for accurate comparison

        // Get all date fields for this row (filter out future dates)
        const dateFields = Object.keys(row)
          .filter(key => DATE_FORMATS.YYYY_MM_DD_REGEX.test(key))
          .filter(dateKey => {
            const date = new Date(dateKey);
            return date <= today; // Only include dates that are today or in the past
          })
          .sort((a, b) => b.localeCompare(a)); // Sort newest to oldest

        if (dateFields.length === 0) return null;

        // Find the most recent date that doesn't have differences with Illio
        const lastMatchingDateField = dateFields.find(dateField => {
          // Check if this field has a value
          const hasValue =
            row[dateField] !== null &&
            row[dateField] !== undefined &&
            row[dateField] !== '';

          // Check if this field has no differences with Illio
          const hasDifference =
            currentUpdatedCells[rowId]?.has(dateField) || false;

          return hasValue && !hasDifference;
        });

        // If no matching date found, return null
        if (!lastMatchingDateField) return null;

        // Calculate days difference between today and the last matching date
        const lastUpdateDate = new Date(lastMatchingDateField);

        const diffTime = Math.abs(today.getTime() - lastUpdateDate.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return diffDays;
      },
      []
    );

  // Track if initial fetch has happened to avoid double fetching on mount
  const hasInitialFetch = useRef(false);

  // Fetch OTC data from API
  const fetchOTCData = useCallback(
    async (includeInactive: boolean = false) => {
      try {
        setLoading(true);
        const response = await Actions.getOTCAssets(includeInactive);
        const data = response.otc_assets || [];

        // Set last sync date from response
        setLastSyncDate(response.last_sync || null);

        // Transform the data with nested prices into flattened rows for the DataGrid
        const dataWithIds = data.map((otc: OTCResponse) => {
          const otcId =
            otc.otc_id || `temp-${Math.random().toString(36).substring(2, 9)}`;
          const flattenedOTC: OTCRow = {
            ...otc,
            id: otcId,
          };

          if (otc.nav_prices && typeof otc.nav_prices === 'object') {
            Object.entries(otc.nav_prices).forEach(([dateKey, value]) => {
              flattenedOTC[dateKey] = value;
            });
            delete flattenedOTC.nav_prices;
          }
          return flattenedOTC;
        });

        // Collect all unique date fields for P&L calculation
        const dateFields = new Set<string>();
        const excludedFields = [
          'id',
          '_id',
          'otc_id',
          'nav_prices',
          'groups',
          'isGroup',
        ];

        dataWithIds.forEach(row => {
          Object.keys(row).forEach(key => {
            if (
              !BASE_COLUMN_FIELDS.includes(key) &&
              !excludedFields.includes(key) &&
              DATE_FORMATS.YYYY_MM_DD_REGEX.test(key)
            ) {
              dateFields.add(key);
            }
          });
        });

        // Sort date fields in descending order (newest first)
        const sortedDateFields = Array.from(dateFields).sort((a, b) =>
          b.localeCompare(a)
        );

        // Calculate P&L for each row
        const dataWithPnL = dataWithIds.map(row => {
          const pnl = calculatePnL(row, sortedDateFields);
          return { ...row, [COLUMN_FIELDS.PNL]: pnl };
        });

        // Set initial data state with days since update calculated
        const dataWithPnLAndDays = dataWithPnL.map(row => ({
          ...row,
          [DAYS_SINCE_ILLIO_UPDATE]: calculateDaysSinceIllioUpdate(
            row,
            getRowId(row),
            {} // Pass empty updatedCells initially
          ),
        }));

        setOTCData(dataWithPnLAndDays as OTCRow[]);

        // Store original data for comparison
        const originalValues: Record<string, OTCRow> = {};
        dataWithPnLAndDays.forEach(row => {
          originalValues[getRowId(row)] = { ...row };
        });
        setOriginalData(originalValues);

        setLoading(false);
        hasInitialFetch.current = true;
      } catch (error) {
        console.error('Error fetching OTC data:', error);
        showSnackbar({
          title: 'Error loading data',
          type: SnackbarType.error,
          message: 'Failed to load OTC data. Please try again.',
        });
        setLoading(false);
      }
    },
    [calculatePnL, calculateDaysSinceIllioUpdate]
  );

  // Refetch when showInactiveInstruments changes (skip initial mount)
  useEffect(() => {
    if (hasInitialFetch.current) {
      fetchOTCData(showInactiveInstruments);
    }
  }, [showInactiveInstruments, fetchOTCData]);

  // Create expandGroups wrapper with current context
  const expandGroups = useExpandGroups(groups, displayData);

  // Row selection handler using shared utility
  const handleRowSelectionChange = useGridSelectionHandler(
    selectedRows,
    setSelectedRows,
    displayData,
    {
      getRowId: row => getRowId(row),
      expandGroups,
    }
  );

  // Prepare available rows for groups modal
  const availableRowsForGroups = useMemo(
    () =>
      otcData.map(row => ({
        ...row, // Include all fields from the row including custom columns
        id: row.id,
        _id: row.id,
        label: `${row.code || 'Unknown'} - ${row.illio_name || 'No Name'}`,
      })),
    [otcData]
  );

  return {
    otcData,
    originalData,
    loading,
    selectedRows,
    lastSyncDate,
    showInactiveInstruments,
    setShowInactiveInstruments,
    groups,
    setGroups,
    isGroupingEnabled,
    isGroupsModalOpen,
    availableRowsForGroups,
    fetchOTCData,
    setOTCData,
    setOriginalData,
    handleRowSelectionChange,
    setSelectedRows,
    handleOpenGroupsModal,
    handleCloseGroupsModal,
    handleSaveGroups,
    setIsGroupingEnabled,
    loadGroupsFromBackend,
    calculatePnL,
    calculateDaysSinceIllioUpdate,
    expandGroups,
  };
};
